import { AmortizationSystem } from "../types/ownerDashboard.types";
import { CreateFinancingPayload } from "../services/ownerPatrimonyService";
import { isCompleteMaskedDate, maskedDisplayToIsoDate } from "./maskedDate";

export interface FinancingFormValues {
  kenloPropertyId: string;
  amortizationSystem: AmortizationSystem;
  paymentStartDateDisplay: string;
  monthlyInstallment: string;
  financedAmount: string;
  annualInterestRatePct: string;
  installmentCount: string;
}

export function parseCurrencyInput(value: string): number {
  return Number(value.replace(/\./g, "").replace(",", "."));
}

export function parseDecimalInput(value: string): number {
  return Number(value.replace(",", "."));
}

export function validateFinancingForm(values: FinancingFormValues): string | null {
  if (!values.kenloPropertyId) {
    return "Selecione um imóvel.";
  }

  if (!isCompleteMaskedDate(values.paymentStartDateDisplay)) {
    return "Informe a data de início dos pagamentos no formato dd/mm/aaaa.";
  }

  if (values.amortizationSystem === "price") {
    const monthlyInstallment = parseCurrencyInput(values.monthlyInstallment);

    if (!monthlyInstallment || monthlyInstallment <= 0) {
      return "Informe uma parcela mensal válida.";
    }

    return null;
  }

  const financedAmount = parseCurrencyInput(values.financedAmount);
  const annualInterestRatePct = parseDecimalInput(values.annualInterestRatePct);
  const installmentCount = Number.parseInt(values.installmentCount, 10);

  if (!financedAmount || financedAmount <= 0) {
    return "Informe o valor financiado.";
  }

  if (!Number.isFinite(annualInterestRatePct) || annualInterestRatePct < 0) {
    return "Informe a taxa de juros anual válida.";
  }

  if (!Number.isInteger(installmentCount) || installmentCount <= 0) {
    return "Informe o número de parcelas.";
  }

  return null;
}

export function buildFinancingPayload(values: FinancingFormValues): CreateFinancingPayload | null {
  const validationError = validateFinancingForm(values);

  if (validationError) {
    return null;
  }

  const paymentStartDate = maskedDisplayToIsoDate(values.paymentStartDateDisplay);

  if (!paymentStartDate) {
    return null;
  }

  if (values.amortizationSystem === "price") {
    return {
      kenlo_property_id: values.kenloPropertyId,
      amortization_system: "price",
      payment_start_date: paymentStartDate,
      monthly_installment: parseCurrencyInput(values.monthlyInstallment),
    };
  }

  return {
    kenlo_property_id: values.kenloPropertyId,
    amortization_system: "sac",
    payment_start_date: paymentStartDate,
    financed_amount: parseCurrencyInput(values.financedAmount),
    annual_interest_rate_pct: parseDecimalInput(values.annualInterestRatePct),
    installment_count: Number.parseInt(values.installmentCount, 10),
  };
}
